/**
 * SPRUCE WATER ENHANCERS — PREMIUM UX ANIMATIONS
 * Scroll reveals, parallax, micro-interactions
 */

// Add sentinel class immediately so CSS can hide elements safely (progressive enhancement)
document.documentElement.classList.add('spruce-js');

if (typeof SpruceAnimations === 'undefined') {

    class SpruceAnimations {
        constructor() {
            this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            this.isMobile = window.innerWidth < 1024;
            this.ticking = false;
            this.init();
        }

        init() {
            this.initScrollReveal();
            this.initStickyATC();
            this.initFAQSmoothAccordion();
            this.initCheckmarkDraw();
            this.initTimelineStepPop();
            this.initUsageReveal();

            if (!this.isMobile) {
                this.initParallax();
                this.initIngredientTilt();
            }

            this.initWaterBackground();

            window.addEventListener('resize', () => {
                this.isMobile = window.innerWidth < 1024;
            }, { passive: true });
        }

        /* =============================================
           IMMERSIVE WATER BACKGROUND
           Water level drops as you scroll down the timeline
           ============================================= */
        initWaterBackground() {
            if (this.prefersReducedMotion) return;

            const waterBg = document.getElementById('spruce-timeline-water-bg');
            const timelineSection = document.querySelector('.spruce-timeline-section');

            if (!waterBg || !timelineSection) return;

            const updateWaterLevel = () => {
                if (this.ticking) return;
                this.ticking = true;

                requestAnimationFrame(() => {
                    const rect = timelineSection.getBoundingClientRect();
                    const windowHeight = window.innerHeight;
                    const sectionHeight = rect.height;
                    const scrollPosition = -rect.top;

                    // Calculate progress: 0 when top of section enters viewport, 1 when bottom leaves
                    // Adjusted to drain mostly while the section is fully in view
                    // Ensure we don't divide by zero if sectionHeight is small
                    const scrollRange = Math.max(1, sectionHeight - windowHeight);
                    let progress = scrollPosition / scrollRange;

                    progress = Math.max(0, Math.min(1, progress));

                    // Height drops from 100% to 5%
                    const waterHeight = 100 - (progress * 95);

                    waterBg.style.height = `${waterHeight}%`;

                    this.ticking = false;
                });
            };

            window.addEventListener('scroll', updateWaterLevel, { passive: true });
            updateWaterLevel();
        }

        /* =============================================
           SCROLL REVEAL
           ============================================= */
        initScrollReveal() {
            const selectors = [
                '.spruce-section-h2',
                '.spruce-section-h3',
                '.spruce-heading-xl',
                '.spruce-card',
                '.spruce-card-benefit',
                '.spruce-stat',
                '.spruce-timeline-step',
                '.spruce-faq-item',
                '[data-reveal]',
            ];

            // Review cards use left/right — handle separately
            const reviewCards = document.querySelectorAll('.spruce-reveal-left, .spruce-reveal-right');

            const elements = document.querySelectorAll(selectors.join(', '));

            // Tag elements and set stagger delays
            elements.forEach((el) => {
                if (el.classList.contains('spruce-reveal-init')) return;
                el.classList.add('spruce-reveal-init', 'spruce-reveal');

                const siblings = Array.from(el.parentElement.children).filter(
                    c => c.classList.contains('spruce-reveal-init')
                );
                const delay = Math.min(siblings.indexOf(el) * 80, 400);
                el.style.transitionDelay = `${delay}ms`;
            });

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                threshold: 0,
                rootMargin: '0px 0px -20px 0px'
            });

            document.querySelectorAll('.spruce-reveal').forEach(el => observer.observe(el));

            // Also observe the review fan-in cards
            const fanObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        fanObserver.unobserve(entry.target);
                    }
                });
            }, { threshold: 0, rootMargin: '0px 0px -20px 0px' });

            reviewCards.forEach(el => fanObserver.observe(el));
        }

        /* =============================================
           PARALLAX — desktop only
           ============================================= */
        initParallax() {
            if (this.prefersReducedMotion) return;

            const parallaxImage = document.querySelector('[data-parallax-image]');
            const parallaxBg = document.querySelector('[data-parallax-bg]');

            if (!parallaxImage && !parallaxBg) return;

            const onScroll = () => {
                if (this.ticking) return;
                this.ticking = true;
                requestAnimationFrame(() => {
                    if (parallaxImage) {
                        const wrap = parallaxImage.closest('[data-parallax-wrap]');
                        if (wrap) {
                            const rect = wrap.getBoundingClientRect();
                            if (rect.bottom > 0 && rect.top < window.innerHeight) {
                                const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * 0.15;
                                parallaxImage.style.transform = `translateY(${offset}px)`;
                            }
                        }
                    }
                    if (parallaxBg) {
                        const rect = parallaxBg.getBoundingClientRect();
                        if (rect.bottom > 0 && rect.top < window.innerHeight) {
                            const offset = (rect.top - window.innerHeight / 2) * 0.25;
                            parallaxBg.style.backgroundPositionY = `calc(50% + ${offset}px)`;
                        }
                    }
                    this.ticking = false;
                });
            };

            window.addEventListener('scroll', onScroll, { passive: true });
            onScroll();
        }

        /* =============================================
           STICKY ATC BAR
           ============================================= */
        initStickyATC() {
            const atcButton = document.querySelector('[data-atc-button]');
            const stickyBar = document.getElementById('spruce-sticky-atc');

            if (!atcButton || !stickyBar) return;

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) {
                        stickyBar.classList.add('is-visible');
                        stickyBar.setAttribute('aria-hidden', 'false');
                    } else {
                        stickyBar.classList.remove('is-visible');
                        stickyBar.setAttribute('aria-hidden', 'true');
                    }
                });
            }, { threshold: 0 });

            observer.observe(atcButton);

            const stickyBtn = stickyBar.querySelector('[data-sticky-atc-btn]');
            if (stickyBtn) {
                stickyBtn.addEventListener('click', () => {
                    const mainForm = document.getElementById('spruce-product-form');
                    if (mainForm) {
                        if (mainForm.requestSubmit) {
                            mainForm.requestSubmit();
                        } else {
                            mainForm.submit();
                        }
                    }
                });
            }
        }

        /* =============================================
           FAQ SMOOTH ACCORDION
           ============================================= */
        initFAQSmoothAccordion() {
            const faqButtons = document.querySelectorAll('[data-faq-toggle]');
            if (!faqButtons.length) return;

            // Set up all answers for smooth animation
            document.querySelectorAll('[data-faq-answer]').forEach(answer => {
                answer.style.maxHeight = '0';
                answer.style.overflow = 'hidden';
                answer.style.transition = 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease';
                answer.style.opacity = '0';
                answer.classList.remove('spruce-hidden');
                answer.dataset.open = 'false';
            });

            faqButtons.forEach(button => {
                button.addEventListener('click', () => {
                    const faqId = button.dataset.faqToggle;
                    const answer = document.getElementById(`faq-answer-${faqId}`);
                    const icon = button.querySelector('[data-faq-icon]');
                    if (!answer) return;

                    const isOpen = answer.dataset.open === 'true';

                    // Close all
                    document.querySelectorAll('[data-faq-answer]').forEach(ans => {
                        ans.style.maxHeight = '0';
                        ans.style.opacity = '0';
                        ans.dataset.open = 'false';
                    });
                    document.querySelectorAll('[data-faq-icon]').forEach(ic => {
                        ic.innerHTML = '<svg style="width:1.5rem;height:1.5rem;color:var(--spruce-slate-900);" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>';
                    });

                    if (!isOpen) {
                        answer.style.maxHeight = answer.scrollHeight + 48 + 'px';
                        answer.style.opacity = '1';
                        answer.dataset.open = 'true';
                        if (icon) {
                            icon.innerHTML = '<svg style="width:1.5rem;height:1.5rem;color:var(--spruce-emerald-600);" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" /></svg>';
                        }
                    }
                });
            });
        }

        /* =============================================
           CHECKMARK DRAW
           ============================================= */
        initCheckmarkDraw() {
            if (this.prefersReducedMotion) return;
            const checkmarks = document.querySelectorAll('.spruce-checkmark-draw');
            if (!checkmarks.length) return;

            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry, i) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => entry.target.classList.add('is-drawn'), i * 80);
                        observer.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.3 });

            checkmarks.forEach(el => observer.observe(el));
        }

        /* =============================================
           TIMELINE STEP POP
           ============================================= */
        initTimelineStepPop() {
            if (this.prefersReducedMotion) return;

            const mutationObserver = new MutationObserver((mutations) => {
                mutations.forEach(mutation => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const step = mutation.target;
                        const icon = step.querySelector('.spruce-timeline-icon, .spruce-timeline-number');
                        if (!icon) return;

                        if (step.classList.contains('active') && !step.dataset.popped) {
                            step.dataset.popped = 'true';
                            icon.classList.add('spruce-pop');
                            setTimeout(() => icon.classList.remove('spruce-pop'), 600);
                        } else if (!step.classList.contains('active')) {
                            delete step.dataset.popped;
                        }
                    }
                });
            });

            document.querySelectorAll('.spruce-timeline-step').forEach(step => {
                mutationObserver.observe(step, { attributes: true });
            });
        }

        /* =============================================
           USAGE IMAGE REVEAL
           ============================================= */
        initUsageReveal() {
            if (this.prefersReducedMotion) return;
            const images = document.querySelectorAll('[data-usage-image]');
            if (!images.length) return;

            images.forEach((img, i) => {
                img.style.clipPath = 'inset(100% 0 0 0)';
                img.style.transition = `clip-path 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${i * 120}ms`;
            });

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.style.clipPath = 'inset(0% 0 0 0)';
                        observer.unobserve(entry.target);
                    }
                });
            }, { threshold: 0.05 });

            images.forEach(img => observer.observe(img));
        }

        /* =============================================
           INGREDIENT CARD TILT — desktop only
           ============================================= */
        initIngredientTilt() {
            if (this.prefersReducedMotion) return;
            const cards = document.querySelectorAll('.spruce-ingredient-card');
            if (!cards.length) return;

            cards.forEach(card => {
                card.style.transition = 'transform 0.15s ease';
                card.style.willChange = 'transform';

                card.addEventListener('mousemove', (e) => {
                    const rect = card.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    card.style.transform = `perspective(600px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
                });

                card.addEventListener('mouseleave', () => {
                    card.style.transform = 'perspective(600px) rotateY(0deg) rotateX(0deg) scale(1)';
                });
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new SpruceAnimations());
    } else {
        new SpruceAnimations();
    }

    window.SpruceAnimations = SpruceAnimations;

} // End re-declaration guard
